import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
    Box,
    GlobalSearch,
    ColorSchemes,
    type GlobalSearchProps,
    Avatar,
} from "@mallardbay/lib-react-components"
import { useMediaQuery } from "usehooks-ts"

import { TEST_IDS } from "~config/constants"
import { useOutfitterContext } from "~contexts/outfitter-provider"
import useDashboardSearch from "~utils/hooks/use-dashboard-search"
import { navigateToRoute } from "~utils/helpers/navigation-helpers"
import type { DashboardSearchGroupFieldsFragment } from "~graphql/dashboard-home.generated"
import { getFeatureIcon } from "~utils/feature-icons"
import {
    DashboardSearchTagColor,
    FeatureIconKey,
} from "~graphql/generated/types"

// We use a custom breakpoint because it's very specific to our use case of it being in the header with other elements
export const MOBILE_SEARCHBAR_BREAKPOINT = "600px"
const SEARCHBAR_MIN_WIDTH = "400px"

export default function GlobalDashboardSearch() {
    const [searchTerm, setSearchTerm] = useState<string>("")
    const shouldUseMobileSearchbar = useMediaQuery(
        `(max-width: ${MOBILE_SEARCHBAR_BREAKPOINT})`
    )
    const { outfitter } = useOutfitterContext()

    const { groups, query, isLoading, error } = useDashboardSearch({
        searchText: searchTerm,
        outfitterId: outfitter?.id ?? "",
    })

    const handleSearchChange = (newValue: string) => {
        setSearchTerm(newValue)
    }

    const handleResultClick = (result: { url: string }) => {
        // Navigate to the result URL
        if (result.url) {
            navigateToRoute(result.url)
        }
    }

    const handleViewMoreClick = (group: { viewMoreUrl: string }) => {
        // Navigate to the group's view more URL
        if (group.viewMoreUrl) {
            navigateToRoute(group.viewMoreUrl)
        }
    }

    const handleClear = () => {
        setSearchTerm("")
    }

    const searchResults = transformSearchResults(groups, query)

    return (
        <Box data-testid={TEST_IDS.SEARCH_CONTAINER}>
            <GlobalSearch
                minWidth={SEARCHBAR_MIN_WIDTH}
                isMobile={shouldUseMobileSearchbar}
                testId={TEST_IDS.SEARCH_INPUT}
                searchInputProps={{
                    value: searchTerm,
                }}
                onChange={handleSearchChange}
                onClear={handleClear}
                searchResults={searchResults}
                onResultClick={handleResultClick}
                onViewMoreClick={handleViewMoreClick}
                isLoading={isLoading}
                error={error}
            />
        </Box>
    )
}

function mapColorScheme(graphqlColor: DashboardSearchTagColor): ColorSchemes {
    switch (graphqlColor) {
        case DashboardSearchTagColor.Blue:
            return ColorSchemes.Blue
        case DashboardSearchTagColor.Green:
            return ColorSchemes.Green
        case DashboardSearchTagColor.Grey:
            return ColorSchemes.Gray
        case DashboardSearchTagColor.Red:
            return ColorSchemes.Red
        case DashboardSearchTagColor.Yellow:
            return ColorSchemes.Orange
        default:
            return ColorSchemes.Gray
    }
}

// Exported for unit testing - this function contains core data transformation logic
// that should be tested independently of Apollo integration and component state
export function transformSearchResults(
    groups: DashboardSearchGroupFieldsFragment[],
    query: string
): GlobalSearchProps["searchResults"] {
    if (groups.length === 0) {
        return undefined
    }

    return {
        query: query,
        groups: groups.map((group) => ({
            ...group,
            viewMoreUrl: group.view_more_url,
            results: group.results.map((result) => ({
                title: result.title,
                subtitle: result.subtitle || undefined,
                url: result.url,
                leftDecorator: getLeftDecorator(result),
                tag: result.tag
                    ? {
                          label: result.tag.label,
                          colorScheme: mapColorScheme(result.tag.color_scheme),
                      }
                    : undefined,
            })),
        })),
    }
}

// Exported for unit testing - this function contains core logic for determining
// the left decorator (image, avatar, or icon) for search results
export function getLeftDecorator(
    result: DashboardSearchGroupFieldsFragment["results"][0]
): string | React.ReactElement | undefined {
    if (result.image_url) {
        return result.image_url
    }

    switch (result.feature_key) {
        case FeatureIconKey.Leads:
        case FeatureIconKey.Clients:
            return (
                <Avatar
                    name={result.title}
                    size="sm"
                    data-testid={TEST_IDS.SEARCH_RESULT_AVATAR}
                />
            )
        default:
            // eslint-disable-next-line no-case-declarations, no-unused-vars -- Valid exception
            const icon = getFeatureIcon(result.feature_key)

            // We future proof this by not returning undefined if the icon is not found
            // This will happen when we add new feature keys that are not yet supported
            // by older versions of the app
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (!icon) return undefined

            return (
                <FontAwesomeIcon
                    icon={icon}
                    data-testid={TEST_IDS.SEARCH_RESULT_ICON}
                />
            )
    }

    return undefined
}

// Do not show if there's no outfitter selected
export function useShouldShowGlobalSearch() {
    const { outfitter } = useOutfitterContext()

    return Boolean(outfitter)
}
